import { useEffect, useState, useCallback, useRef } from "react";
import { sfx } from "../../lib/soundEngine";

const ICONS = [
    { name: "gamepad", cls: "fa-gamepad", color: "var(--neon-cyan)" },
    { name: "ghost", cls: "fa-ghost", color: "var(--neon-pink)" },
    { name: "heart", cls: "fa-heart", color: "var(--neon-pink)" },
    { name: "rocket", cls: "fa-rocket", color: "var(--neon-amber)" },
    { name: "skull", cls: "fa-skull", color: "var(--neon-lime)" },
    { name: "star", cls: "fa-star", color: "var(--neon-amber)" },
    { name: "bolt", cls: "fa-bolt", color: "var(--neon-amber)" },
    { name: "diamond", cls: "fa-gem", color: "var(--neon-cyan)" },
];

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const buildDeck = () => {
    const deck = [...ICONS, ...ICONS].map((icon, i) => ({
        uid: i,
        ...icon,
        matched: false,
    }));
    return shuffle(deck);
};

export const MemoryMatchGame = () => {
    const [deck, setDeck] = useState(buildDeck());
    const [flipped, setFlipped] = useState([]);
    const [locked, setLocked] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [moves, setMoves] = useState(0);
    const [matched, setMatched] = useState(0);
    const [won, setWon] = useState(false);
    const previewTimerRef = useRef(null);

    const startPreview = useCallback(() => {
        setPreviewing(true);
        setLocked(true);
        setCountdown(4);
        sfx.reveal();
        let n = 4;
        const iv = setInterval(() => {
            n -= 1;
            if (n <= 0) {
                clearInterval(iv);
                setCountdown(0);
                setPreviewing(false);
                setLocked(false);
            } else {
                setCountdown(n);
                sfx.tick();
            }
        }, 1000);
        previewTimerRef.current = iv;
    }, []);

    const reset = useCallback(() => {
        if (previewTimerRef.current) clearInterval(previewTimerRef.current);
        setDeck(buildDeck());
        setFlipped([]);
        setMoves(0);
        setMatched(0);
        setWon(false);
        sfx.coin();
        startPreview();
    }, [startPreview]);

    useEffect(() => {
        startPreview();
        return () => {
            if (previewTimerRef.current) clearInterval(previewTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (uid) => {
        if (locked || previewing) return;
        const card = deck.find((c) => c.uid === uid);
        if (!card || card.matched || flipped.includes(uid)) return;
        sfx.click();
        const nextFlipped = [...flipped, uid];
        setFlipped(nextFlipped);
        if (nextFlipped.length === 2) {
            setLocked(true);
            setMoves((m) => m + 1);
            const [a, b] = nextFlipped.map((u) => deck.find((c) => c.uid === u));
            if (a.name === b.name) {
                setTimeout(() => {
                    setDeck((prev) => prev.map((c) => (c.uid === a.uid || c.uid === b.uid ? { ...c, matched: true } : c)));
                    setMatched((m) => {
                        const next = m + 1;
                        if (next === ICONS.length) {
                            setWon(true);
                            sfx.win();
                        } else {
                            sfx.match();
                        }
                        return next;
                    });
                    setFlipped([]);
                    setLocked(false);
                }, 320);
            } else {
                setTimeout(() => {
                    setFlipped([]);
                    setLocked(false);
                    sfx.fail();
                }, 700);
            }
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between font-pixel text-lg">
                <span className="neon-cyan" data-testid="memory-moves">MOVES: {moves}</span>
                <span className="neon-amber" data-testid="memory-matched">PAIRS: {matched}/{ICONS.length}</span>
            </div>

            <div className="relative">
                <div className="grid grid-cols-4 gap-2 mx-auto" style={{ width: "100%", maxWidth: 360 }}>
                    {deck.map((card) => {
                        const showFront = previewing || flipped.includes(card.uid) || card.matched;
                        return (
                            <div
                                key={card.uid}
                                data-testid={`memory-card-${card.uid}`}
                                className="card-flip"
                                style={{ aspectRatio: "1", cursor: previewing || locked ? "default" : "pointer" }}
                                onClick={() => handleClick(card.uid)}
                            >
                                <div className={`card-inner ${showFront ? "flipped" : ""}`}>
                                    <div className="card-face card-back">
                                        <i className="fa-solid fa-question neon-amber text-2xl" />
                                    </div>
                                    <div className={`card-face card-front ${card.matched ? "matched" : ""}`}>
                                        <i
                                            className={`fa-solid ${card.cls}`}
                                            style={{
                                                color: card.color,
                                                fontSize: "1.75rem",
                                                textShadow: `0 0 8px ${card.color}, 0 0 18px ${card.color}`,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {previewing && (
                    <div
                        data-testid="memory-preview-banner"
                        className={`absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full font-pixel text-lg neon-amber whitespace-nowrap ${countdown === 0 ? "fade-out" : "fade-in"}`}
                    >
                        MEMORIZE... {countdown}
                    </div>
                )}
            </div>

            <div className="flex justify-center gap-3 flex-wrap">
                <button onClick={reset} data-testid="memory-reset-btn" className="btn-neon btn-neon-amber">
                    <i className="fa-solid fa-rotate-right" /> New Deck
                </button>
                {won && (
                    <span data-testid="memory-win-badge" className="font-pixel text-xl neon-lime self-center">
                        <i className="fa-solid fa-trophy mr-2" /> CLEARED!
                    </span>
                )}
            </div>
        </div>
    );
};
